'use client'

import { useEffect } from 'react'

import { FormattedMessage } from 'react-intl'
import { styled } from 'styled-components'

import { PositiveIntegerInputBordered } from '@b-stock/fe-core/bstock-react'

import { useTrackFilterUsage } from '../analytics'
import type { Range } from '../commonTypes'
import WithDynamicButton from '../WithDynamicApplyButton'
import useFreeEntryRange from './useFreeEntryRange'

const Container = styled.div`
  max-width: 11.75rem;
  display: grid;
  grid-template-columns: 50% 50%;
  grid-gap: 0.625rem 0.5rem;
`

const Error = styled.div`
  grid-column: 1 / 3;
`

const FreeEntryContainer = styled.div`
  & input {
    width: 100%;
  }
`

const IntegerRangeFilter = ({
  selected,
  onChange,
  onError,
  desktopScreen = true,
  analyticsTitle,
}: {
  selected: Range
  onChange: (value: Range) => void
  desktopScreen?: boolean
  onError?: (value: boolean) => void
  analyticsTitle: string
}) => {
  const { freeEntryRange, setMin, setMax } = useFreeEntryRange({
    selected,
    onChange,
    desktopScreen,
  })
  const showResultBtnVisible =
    desktopScreen && freeEntryRange.some((val, i) => val !== selected[i])
  const invalidMax =
    freeEntryRange[1] !== null &&
    freeEntryRange[0] !== null &&
    freeEntryRange[1] < freeEntryRange[0]

  const trackFilterUsage = useTrackFilterUsage<{
    filter_title: string
    min_value: number | null
    max_value: number | null
  }>('filters')

  const handleShowResultsClick = () => {
    trackFilterUsage({
      filter_title: analyticsTitle,
      min_value: freeEntryRange[0],
      max_value: freeEntryRange[1],
    })

    onChange(freeEntryRange)
  }

  useEffect(() => {
    onError?.(invalidMax)
    // We want onError to get called whenever `invalidMax` changes.
    // onError is likely stable, but we don't strictly care if it isn't
    // If it ever wasn't stable, we wouldn't want to re-trigger the callback
    // every single render.
    // oxlint-disable-next-line react/exhaustive-deps
  }, [invalidMax])

  return (
    <WithDynamicButton
      buttonContent={
        <FormattedMessage id="Filters.numericRange.filterButton" />
      }
      isButtonVisible={showResultBtnVisible}
      onClick={handleShowResultsClick}
      isButtonDisabled={invalidMax}
    >
      <Container>
        <FreeEntryContainer>
          <PositiveIntegerInputBordered
            label={<FormattedMessage id="Filters.numericRange.min" />}
            value={freeEntryRange[0]}
            onChange={setMin}
            // A "0" min is the same as leaving it blank... so it's not
            // strictly necessary to allow zero... but some users might
            // feel more comfortable typing in an explicit zero instead
            // of leaving min blank if they only care about the max
            allowZero
          />
        </FreeEntryContainer>

        <FreeEntryContainer>
          <PositiveIntegerInputBordered
            label={<FormattedMessage id="Filters.numericRange.max" />}
            value={freeEntryRange[1]}
            onChange={setMax}
            error={invalidMax}
          />
        </FreeEntryContainer>

        {invalidMax ? (
          <Error>
            <PositiveIntegerInputBordered.ErrorMessage>
              <FormattedMessage id="Filters.BaseFreeEntryInput.hasError" />
            </PositiveIntegerInputBordered.ErrorMessage>
          </Error>
        ) : null}
      </Container>
    </WithDynamicButton>
  )
}

export default IntegerRangeFilter
